import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
import './GenieJournal.css';

/**
 * 📓 Le journal du Génie — la MÉMOIRE + la COMPARAISON.
 * Chaque action passée (concours, campagne) revient avec son résultat réel
 * et sa comparaison à la précédente du même type. Le Génie « guide » (Dashboard)
 * reste la SEULE voix qui propose la suite — ici, on mesure et on compare.
 * Défensif : rend null s'il n'y a rien à montrer, ne casse jamais la page.
 */
export default function GenieJournal() {
  const { campaigns } = useApp();
  const [concours, setConcours] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.getLandingPages()
      .then(list => { if (alive) setConcours((list || []).filter(p => p.template === 'concours' && p.published_at)); })
      .catch(() => { if (alive) setConcours([]); });
    return () => { alive = false; };
  }, []);

  const entries = (() => {
    const out = [];
    // Campagnes envoyées
    (campaigns || []).filter(c => c.status === 'sent' && c.sent_at).forEach(c => {
      const sample = Number(c.contacts) || 0;
      const rate = Number(c.open_rate ?? (sample > 0 ? Math.round((c.opens || 0) / sample * 100) : 0)) || 0;
      out.push({
        kind: 'campagne', id: `c${c.id}`, icon: '📣', title: c.name || 'Campagne',
        date: new Date(c.sent_at), sample,
        metrics: [
          { v: sample, lbl: '📧 envoyés' },
          { v: `${rate}%`, lbl: '👁 ouverts', hot: rate >= 35 },
          ...(c.feedback ? [{ v: c.feedback, lbl: '💛 réactions', hot: true }] : []),
        ],
        key: rate, keyKind: 'rate',
      });
    });
    // Concours publiés
    (concours || []).forEach(p => {
      const leads = p.leads_count ?? 0;
      out.push({
        kind: 'concours', id: `p${p.id}`, icon: '🎁',
        title: (p.config?.prize_name || p.title || 'Concours').replace(/^[^\p{L}\p{N}]+\s*/u, ''),
        date: new Date(p.winner_drawn_at || p.published_at), sample: leads,
        metrics: [
          { v: leads, lbl: '🎂 contacts gagnés', hot: leads > 0 },
          { v: p.visits_count ?? 0, lbl: '👀 visites' },
        ],
        key: leads, keyKind: 'leads',
      });
    });
    out.sort((a, b) => b.date - a.date);
    // Comparaison : chaque action vs la précédente du MÊME type
    out.forEach((e, i) => {
      const prev = out.slice(i + 1).find(x => x.kind === e.kind);
      // Campagnes : ne comparer que des envois d'échantillon comparable (≥10 destinataires),
      // sinon un taux sur 5 personnes crée des écarts trompeurs (« -81 % »).
      const tiny = e.kind === 'campagne' && (e.sample < 10 || (prev && prev.sample < 10));
      if (!prev || !(prev.key > 0) || tiny) { e.cmp = { type: prev ? 'base' : 'first' }; return; }
      const delta = Math.round((e.key - prev.key) / prev.key * 100);
      if (!Number.isFinite(delta)) { e.cmp = { type: 'base' }; return; }
      e.cmp = { type: delta >= 8 ? 'up' : delta <= -8 ? 'down' : 'flat', delta };
    });
    // Verdict déterministe
    out.forEach(e => {
      if (e.kind === 'concours') {
        e.verdict = e.key > 0
          ? 'Le levier le plus rapide pour faire grandir ta base — chaque participant entre avec son anniversaire.'
          : 'Peu de participations cette fois : soigne le lot et pose l\'affiche QR au comptoir.';
      } else {
        e.verdict = e.keyKind === 'rate' && e.key >= 35 ? 'Très bien lue — ce type de message fonctionne, garde le cap.'
          : e.key >= 20 ? 'Correcte. Teste un autre créneau (soir ≠ matin) pour grimper.'
          : 'Peu ouverte : change l\'objet et l\'heure d\'envoi la prochaine fois.';
      }
    });
    return out;
  })();

  if (concours === null) return null;              // en cours de chargement
  if (entries.length < 1) return null;             // rien à raconter encore

  const cmpLabel = (cmp) => {
    if (!cmp || cmp.type === 'first') return null;
    if (cmp.type === 'base') return <span className="gj-cmp base">point de référence</span>;
    if (cmp.type === 'flat') return <span className="gj-cmp flat">▬ stable vs la précédente</span>;
    const up = cmp.type === 'up';
    return <span className={`gj-cmp ${up ? 'up' : 'down'}`}>{up ? '▲' : '▼'} {up ? '+' : ''}{cmp.delta}% vs la précédente</span>;
  };

  return (
    <div className="gj">
      <button type="button" className="gj-head gj-head--btn" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="gj-g">🧞</span>
        <div className="gj-headtxt">
          <div className="gj-title">📓 Le journal du Génie</div>
          <div className="gj-sub">{open
            ? 'Ce que chaque action a donné — et comment elle se compare aux précédentes.'
            : `${entries.length} action${entries.length > 1 ? 's' : ''} mesurée${entries.length > 1 ? 's' : ''} — déplie pour voir le détail`}</div>
        </div>
        <span className="gj-chev" aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </button>
      {open && (
      <div className="gj-timeline">
        {entries.slice(0, 6).map(e => (
          <div key={e.id} className={`gj-entry ${e.kind}`}>
            <div className="gj-top">
              <span className="gj-ic">{e.icon}</span>
              <span className="gj-name">{e.title}</span>
              <span className="gj-date">{format(e.date, 'd MMM', { locale: fr })}</span>
            </div>
            <div className="gj-metrics">
              {e.metrics.map((m, i) => (
                <span key={i} className={`gj-m ${m.hot ? 'hot' : ''}`}><b>{m.v}</b> {m.lbl}</span>
              ))}
              {cmpLabel(e.cmp)}
            </div>
            <div className="gj-verdict"><span className="gj-vg">🧞</span><span>{e.verdict}</span></div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
